import { createContext, useContext, useMemo } from 'react'
import type { ViewStyle, TextStyle, ImageStyle } from 'react-native'

import { isMobile, isStorybook, isCompiledForWeb } from './config'

import type {
  ComponentStyleSheet,
  ComponentState,
  ThemeContextType,
  Theme,
  ComponentThemes,
  ComponentTheme,
  ComponentThemeFactory,
  LayoutsOfTheme,
  DecorationsOfTheme,
  Size,
} from './types'

export { default as useThemedStylesheet } from './useThemedStylesheet'
export { isMobile, isStorybook } from './config'

const defaultThemeContext = {}
export const ThemeContext = createContext<ThemeContextType>(
  defaultThemeContext as ThemeContextType,
)

// this eslint disable is because isCompiledForWeb isn't really a constant false
// it's actually a compile time variable based on whether we're .web.ts or not
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
export const isWeb = isCompiledForWeb && !isStorybook

export function useTheme(): Theme {
  const { theme } = useContext(ThemeContext)
  return useMemo(
    () => ({
      ...theme,
      isMobile: theme.isMobile ?? isMobile,
      isWeb: theme.isWeb ?? isWeb,
    }),
    [theme],
  )
}

export const cssGradientBackground = (gradientDef: string[][]): string =>
  gradientDef
    .map((layer) =>
      !Array.isArray(layer) ? layer : `linear-gradient(${layer.join(', ')})`,
    )
    .join(', ')

type Rule = {
  layout?: string
  decoration?: string
  state?: ComponentState
  size?: Size
  style: ViewStyle | TextStyle | ImageStyle
}

export function mergeComponentTheme<T extends ComponentTheme<any, any, any>>(
  base: T,
  overrides: T,
): T {
  return Object.fromEntries(
    Object.entries(base).map(([key, value]) => [
      key,
      [...(value ?? []), ...(overrides[key] ?? [])],
    ]),
  ) as T
}

export function mergeComponentThemes<T extends ComponentThemes>(
  base: T,
  overrides: T,
): T {
  return Object.fromEntries(
    Object.entries(base).map(([key, value]) => [
      key,
      overrides[key] ? mergeComponentTheme(value, overrides[key]) : value,
    ]),
  ) as T
}

const universals = ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
  !l && !d && !s && !sz
const matchLayout =
  (layout?: string) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout && l === layout && !d && !s && !sz
const matchDecoration =
  (decoration?: string) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l && decoration && d === decoration && !s && !sz
const matchState =
  (state?: ComponentState) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l && !d && state && s === state && !sz
const matchSize =
  (size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l && !d && !s && size && sz === size
const matchLayoutAndDecoration =
  (layout?: string, decoration?: string) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout && l === layout && decoration && d === decoration && !s && !sz
const matchLayoutAndState =
  (layout?: string, state?: ComponentState) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout && l === layout && !d && state && s === state && !sz
const matchLayoutAndSize =
  (layout?: string, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout && l === layout && !d && !s && size && sz === size
const matchDecorationAndState =
  (decoration?: string, state?: ComponentState) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l && decoration && d === decoration && state && s === state && !sz
const matchDecorationAndSize =
  (decoration?: string, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l && decoration && d === decoration && !s && size && sz === size
const matchStateAndSize =
  (state?: ComponentState, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l && !d && state && s === state && size && sz === size
const matchLayoutAndDecorationAndState =
  (layout?: string, decoration?: string, state?: ComponentState) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout &&
    l === layout &&
    decoration &&
    d === decoration &&
    state &&
    s === state &&
    !sz
const matchLayoutAndStateAndSize =
  (layout?: string, state?: ComponentState, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout && l === layout && !d && state && s === state && size && sz === size
const matchLayoutAndDecorationAndSize =
  (layout?: string, decoration?: string, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout &&
    l === layout &&
    decoration &&
    d === decoration &&
    !s &&
    size &&
    sz === size
const matchLayoutDecorationAndState =
  (layout?: string, decoration?: string, state?: ComponentState) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout &&
    l === layout &&
    decoration &&
    d === decoration &&
    !sz &&
    state &&
    s === state
const matchDecorationAndStateAndSize =
  (decoration?: string, state?: ComponentState, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    !l &&
    decoration &&
    d === decoration &&
    state &&
    s === state &&
    size &&
    sz === size

const matchAll =
  (layout?: string, decoration?: string, state?: ComponentState, size?: Size) =>
  ({ layout: l, decoration: d, state: s, size: sz }: Rule) =>
    layout &&
    l === layout &&
    decoration &&
    d === decoration &&
    state &&
    s === state &&
    size &&
    sz === size

export function useComponentStyle<Name extends keyof ComponentThemes>(
  name: Name,
  baseThemeFactory: ComponentThemeFactory<ComponentThemes[Name]>,
  overrides?: ComponentStyleSheet<ComponentThemes[Name]>,
  layout?: LayoutsOfTheme<ComponentThemes[Name]>[number],
  decoration?: DecorationsOfTheme<ComponentThemes[Name]>[number],
  state?: ComponentState,
  size?: Size,
): ComponentStyleSheet<ComponentThemes[Name]> {
  const theme = useTheme()

  return useMemo(() => {
    const mergedTheme =
      theme.components[name] ?
        mergeComponentTheme(baseThemeFactory(theme), theme.components[name])
      : baseThemeFactory(theme)

    return Object.fromEntries(
      Object.entries(mergedTheme).map(([key, value]) => [
        key,
        [
          value?.filter(universals).map(({ style }) => style),
          value?.filter(matchLayout(layout)).map(({ style }) => style),
          value?.filter(matchDecoration(decoration)).map(({ style }) => style),
          value?.filter(matchState(state)).map(({ style }) => style),
          value?.filter(matchSize(size)).map(({ style }) => style),
          value
            ?.filter(matchLayoutAndDecoration(layout, decoration))
            .map(({ style }) => style),
          value
            ?.filter(matchLayoutAndSize(layout, size))
            .map(({ style }) => style),
          value
            ?.filter(matchLayoutAndState(layout, state))
            .map(({ style }) => style),
          value
            ?.filter(matchDecorationAndState(decoration, state))
            .map(({ style }) => style),
          value
            ?.filter(matchDecorationAndSize(decoration, size))
            .map(({ style }) => style),
          value
            ?.filter(matchStateAndSize(state, size))
            .map(({ style }) => style),
          value
            ?.filter(
              matchLayoutAndDecorationAndState(layout, decoration, state),
            )
            .map(({ style }) => style),
          value
            ?.filter(matchLayoutAndStateAndSize(layout, state, size))
            .map(({ style }) => style),
          value
            ?.filter(matchLayoutAndDecorationAndSize(layout, decoration, size))
            .map(({ style }) => style),
          value
            ?.filter(matchLayoutDecorationAndState(layout, decoration, state))
            .map(({ style }) => style),
          value
            ?.filter(matchDecorationAndStateAndSize(decoration, state, size))
            .map(({ style }) => style),
          value
            ?.filter(matchAll(layout, decoration, state, size))
            .map(({ style }) => style),
          overrides && overrides[key],
        ]
          .flat()
          .reduce(
            (acc, val) => ({
              ...acc,
              ...val,
            }),
            {},
          ),
      ]),
    ) as ComponentStyleSheet<ComponentThemes[Name]>
  }, [
    baseThemeFactory,
    theme,
    name,
    layout,
    decoration,
    state,
    size,
    overrides,
  ])
}
